import { Create, Edit, LoadingIndicator, NumberInput, required, Resource, TextInput, useRedirect } from "react-admin";
import { Form2 } from "../misc/Form2";
import { EurosInput } from "../inputs/MoneyInput";
import { useCurrentDrivingSchoolId } from "../hooks/useCurrentDrivingSchoolId";
import { DeleteRecordButton } from "../buttons/DeleteRecordButton";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { Box } from "@mui/material";
import { Column } from "../misc/Column";
import { BackofficeRecordTitle } from "../misc/BackofficeRecordTitle";
import { useRecordAndSwitchDrivingSchoolIfNecessary } from "../hooks/useRecordAndSwitchDrivingSchoolIfNecessary";
import { DrivingSchoolServiceType } from "../providers/drivingSchoolServiceTypesProvider";
import { DefaultList } from "../misc/DefaultList";
import { SaveButton2 } from "../buttons/SaveButton2";
import { overviewCardStyle } from "../misc/backofficeTheme";
import { grants } from "../backoffice.access_control";
import { AccessDenied } from "../misc/AccessDenied";
import { Row } from "../misc/Row";
import { RecordDetailsHeader } from "../misc/RecordDetailsHeader";

function CreateServiceTypeForm() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const redirect = useRedirect();

  if (!grants.has("ServiceType:create")) {
    return <AccessDenied />;
  }

  if (!drivingSchoolId) {
    return (
      <Box className="centered">
        <Column sx={{ alignItems: "center" }}>Bitte wähle eine Fahrschule aus.</Column>
      </Box>
    );
  }

  return (
    <Create
      resource="serviceTypes"
      title={<BackofficeRecordTitle />}
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Rechnungstyp erfolgreich erstellt.");
          redirect(`/drivingSchools/${drivingSchoolId}/serviceTypes`);
        },
        onError: (error: Error) => {
          showErrorToast("Fehler beim Erstellen des Rechnungstyps.");
          console.error("Failed to create drivingSchoolServiceType.", error);
        },
      }}
      transform={(data: Omit<DrivingSchoolServiceType, "drivingSchoolId">): Partial<DrivingSchoolServiceType> => ({
        ...data,
        drivingSchoolId,
      })}
    >
      <DrivingSchoolServiceTypeForm mode="create" />
    </Create>
  );
}

function EditServiceTypeForm() {
  const redirect = useRedirect();
  const serviceType = useRecordAndSwitchDrivingSchoolIfNecessary<DrivingSchoolServiceType>((it) => {
    const { drivingSchoolId } = it;
    if (!drivingSchoolId) {
      throw new Error(`ServiceType ${it.id} has no drivingSchoolId`);
    }
    return drivingSchoolId;
  });

  if (!grants.has("ServiceType:edit")) {
    return <AccessDenied />;
  }

  if (!serviceType) {
    return <LoadingIndicator />;
  }

  return (
    <Edit
      resource="serviceTypes"
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Rechnungstyp erfolgreich aktualisiert.");
          redirect(`/drivingSchools/${serviceType.drivingSchoolId}/serviceTypes`);
        },
        onError: (error: unknown) => {
          showErrorToast("Fehler beim Aktualisieren des Rechnungstyps.");
          console.error("Failed to update drivingSchoolServiceType.", error);
        },
      }}
      mutationMode="pessimistic"
      title={
        <BackofficeRecordTitle
          header={
            <RecordDetailsHeader
              backLabel="Rechnungstypen"
              backUrl={`/drivingSchools/${serviceType.drivingSchoolId}/serviceTypes`}
              title={serviceType.title}
            />
          }
        />
      }
    >
      <DrivingSchoolServiceTypeForm mode="edit" />
    </Edit>
  );
}

function DrivingSchoolServiceTypeForm(props: { mode: "create" | "edit" }) {
  const { mode } = props;
  return (
    <Form2 warnWhenUnsavedChanges>
      <Column sx={{ padding: overviewCardStyle.padding }}>
        <TextInput
          source="title"
          label="Titel"
          autoFocus={mode === "create"}
          validate={required()}
          sx={{ width: "40%" }}
        />
        <NumberInput
          source="vatRate"
          label="MwSt."
          validate={required()}
          defaultValue={19}
          readOnly
          sx={{ width: "40%" }}
        />
        <EurosInput source="price" label="Standard Preis" sx={{ width: "40%" }} size="small" />
      </Column>
      <_Toolbar mode={mode} />
    </Form2>
  );
}

function _Toolbar(props: { mode: "create" | "edit" }) {
  const { mode } = props;
  const drivingSchoolId = useCurrentDrivingSchoolId();
  return (
    <Row sx={{ justifyContent: "space-between", padding: 2 }}>
      <SaveButton2 />
      {mode === "edit" && grants.has("ServiceType:delete") && (
        <DeleteRecordButton onSuccessRedirectTo={`/drivingSchools/${drivingSchoolId}/serviceTypes`} />
      )}
    </Row>
  );
}

export const drivingSchoolServiceTypeResource = (
  <Resource
    key="serviceTypes"
    name="serviceTypes"
    list={DefaultList}
    create={CreateServiceTypeForm}
    edit={EditServiceTypeForm}
    options={{ label: "Rechnungstypen", createTitle: "Neuer Rechnungstyp" }}
    recordRepresentation={(serviceType: DrivingSchoolServiceType) => serviceType.title}
  />
);
